import React, { useEffect, useMemo, useState } from 'react';
import { Button, Checkbox, Modal, Table, Tag, Typography, message } from 'antd';
import { EditOutlined } from '@ant-design/icons';
import { roleService, Role } from '../services/roles';
import { permissionService, Permission } from '../services/permissions';

const { Title, Text } = Typography;

export const Roles: React.FC = () => {
  const [roles, setRoles] = useState<Role[]>([]);
  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [loading, setLoading] = useState(false);
  const [editingRole, setEditingRole] = useState<Role | null>(null);
  const [selectedPermissionIds, setSelectedPermissionIds] = useState<number[]>([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [rolesData, permissionsData] = await Promise.all([
        roleService.getAll(),
        permissionService.getAll(),
      ]);
      setRoles(rolesData);
      setPermissions(permissionsData);
    } catch {
      message.error('Failed to load roles and permissions');
    } finally {
      setLoading(false);
    }
  };

  const permissionGroups = useMemo(() => {
    const groups = new Map<string, Permission[]>();
    permissions.forEach((permission) => {
      const [module] = permission.name.split('.', 1);
      const key = module ?? 'misc';
      if (!groups.has(key)) {
        groups.set(key, []);
      }
      groups.get(key)?.push(permission);
    });
    return Array.from(groups.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([module, items]) => ({
        module,
        items: items.sort((a, b) => a.name.localeCompare(b.name)),
      }));
  }, [permissions]);

  const openEditor = (role: Role) => {
    if (role.name === 'owner') {
      message.info('Owner has full access by default.');
      return;
    }
    setEditingRole(role);
    setSelectedPermissionIds(role.permissions?.map((p) => p.id) ?? []);
  };

  const handleSave = async () => {
    if (!editingRole) {
      return;
    }
    setSaving(true);
    try {
      await roleService.updatePermissions(editingRole.id, selectedPermissionIds);
      message.success('Role permissions updated');
      setEditingRole(null);
      await fetchData();
    } catch {
      message.error('Failed to update permissions');
    } finally {
      setSaving(false);
    }
  };

  const columns = [
    {
      title: 'Role',
      dataIndex: 'name',
      key: 'name',
      render: (value: string) => <Text strong>{value}</Text>,
    },
    {
      title: 'Permissions',
      dataIndex: 'permissions',
      key: 'permissions',
      render: (value: Role['permissions']) => (
        <div>
          {(value ?? []).slice(0, 6).map((permission) => (
            <Tag key={permission.id}>{permission.name}</Tag>
          ))}
          {(value ?? []).length > 6 ? (
            <Tag>+{(value ?? []).length - 6} more</Tag>
          ) : null}
        </div>
      ),
    },
    {
      title: 'Action',
      key: 'action',
      width: 140,
      render: (_: unknown, role: Role) => (
        <Button
          icon={<EditOutlined />}
          onClick={() => openEditor(role)}
          disabled={role.name === 'owner'}
        >
          Edit
        </Button>
      ),
    },
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <Title level={3} style={{ margin: 0 }}>
          Roles
        </Title>
      </div>
      <Table
        columns={columns}
        dataSource={roles}
        rowKey="id"
        loading={loading}
        pagination={false}
      />
      <Modal
        title={editingRole ? `Edit permissions - ${editingRole.name}` : 'Edit permissions'}
        open={!!editingRole}
        onCancel={() => setEditingRole(null)}
        onOk={handleSave}
        okButtonProps={{ loading: saving }}
      >
        <Checkbox.Group
          style={{ width: '100%' }}
          value={selectedPermissionIds}
          onChange={(values) => setSelectedPermissionIds(values as number[])}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {permissionGroups.map((group) => (
              <div key={group.module}>
                <Text strong style={{ display: 'block', marginBottom: 8 }}>
                  {group.module}
                </Text>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: 8 }}>
                  {group.items.map((permission) => {
                    const action = permission.name.split('.', 2)[1] ?? permission.name;
                    return (
                      <Checkbox key={permission.id} value={permission.id}>
                        {action}
                      </Checkbox>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </Checkbox.Group>
      </Modal>
    </div>
  );
};
